import { useState, useMemo } from 'react';
import { Search, ChevronUp, ChevronDown } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCompetenceMatrix, type MatrixEntry } from '@/hooks/use-competence-matrix';
import { useCurrentUser } from '@/hooks/use-current-user';

const CATEGORY_COLORS: Record<string, string> = {
  skill: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  methodology: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  leadership: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  industry: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  tool: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400'
};

const ALL_CATEGORIES = ['all', 'skill', 'methodology', 'leadership', 'industry', 'tool'] as const;
type CategoryFilter = (typeof ALL_CATEGORIES)[number];

type SortField = 'name' | 'totalSkills';
type SortDir = 'asc' | 'desc';

function seniorityBadgeClass(seniority: string | null): string {
  switch (seniority?.toLowerCase()) {
    case 'senior':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400';
    case 'mid':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400';
    case 'junior':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  }
}

function categoryColorClass(category: string): string {
  const key = category.toLowerCase();
  return CATEGORY_COLORS[key] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
}

function matchesSearch(entry: MatrixEntry, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  if (entry.name.toLowerCase().includes(q)) return true;
  for (const skills of Object.values(entry.skillsByCategory)) {
    if (skills.some((s) => s.toLowerCase().includes(q))) return true;
  }
  return false;
}

function filterSkillsByCategory(
  skillsByCategory: Record<string, string[]>,
  categoryFilter: CategoryFilter
): Record<string, string[]> {
  if (categoryFilter === 'all') return skillsByCategory;
  return Object.fromEntries(
    Object.entries(skillsByCategory).filter(([cat]) => cat.toLowerCase() === categoryFilter)
  );
}

export function CompetenceMatrixPage() {
  const t = useT();
  const { canAccessTeam } = useCurrentUser();
  const { data, isLoading, isError } = useCompetenceMatrix();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const matrix = data?.data ?? [];

  const sorted = useMemo(() => {
    const filtered = matrix.filter((entry) => matchesSearch(entry, search));
    return [...filtered].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'name') {
        return a.name.localeCompare(b.name) * dir;
      }
      return (a.totalSkills - b.totalSkills) * dir;
    });
  }, [matrix, search, sortField, sortDir]);

  if (!canAccessTeam) {
    return (
      <div className="py-12 text-center">
        <h2 className="text-lg font-semibold">Access Restricted</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          You don't have permission to view this page. Contact your admin for access.
        </p>
      </div>
    );
  }

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ChevronUp className="inline h-3.5 w-3.5" />
    ) : (
      <ChevronDown className="inline h-3.5 w-3.5" />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('matrix.title')}</h1>
        <span className="text-sm text-muted-foreground">
          {sorted.length} {t('matrix.teamMembers')}
        </span>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('matrix.searchPlaceholder')}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ALL_CATEGORIES.map((cat) => (
            <Button
              key={cat}
              size="sm"
              variant={categoryFilter === cat ? 'default' : 'outline'}
              onClick={() => setCategoryFilter(cat)}
              className="capitalize"
            >
              {cat}
            </Button>
          ))}
        </div>
      </div>

      {/* Table */}
      {isLoading && (
        <div className="py-12 text-center text-sm text-muted-foreground">{t('matrix.loading')}</div>
      )}

      {isError && (
        <div className="py-12 text-center text-sm text-red-500 dark:text-red-400">
          {t('matrix.error')}
        </div>
      )}

      {!isLoading && !isError && sorted.length === 0 && (
        <div className="py-12 text-center text-sm text-muted-foreground">{t('matrix.empty')}</div>
      )}

      {!isLoading && !isError && sorted.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th
                  className="cursor-pointer whitespace-nowrap px-4 py-3 text-left font-medium text-muted-foreground hover:text-foreground"
                  onClick={() => handleSort('name')}
                >
                  Name <SortIcon field="name" />
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-medium text-muted-foreground">
                  Location
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-medium text-muted-foreground">
                  Seniority
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Skills</th>
                <th
                  className="cursor-pointer whitespace-nowrap px-4 py-3 text-right font-medium text-muted-foreground hover:text-foreground"
                  onClick={() => handleSort('totalSkills')}
                >
                  Total <SortIcon field="totalSkills" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-white dark:bg-card">
              {sorted.map((entry) => {
                const visibleSkills = filterSkillsByCategory(
                  entry.skillsByCategory,
                  categoryFilter
                );
                return (
                  <tr key={entry.id} className="hover:bg-muted/30 transition-colors">
                    <td className="whitespace-nowrap px-4 py-3 font-medium">{entry.name}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {entry.location ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {entry.seniority ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${seniorityBadgeClass(entry.seniority)}`}
                        >
                          {entry.seniority}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(visibleSkills).map(([cat, skills]) =>
                          skills.map((skill) => (
                            <Badge
                              key={`${cat}-${skill}`}
                              className={`text-xs font-normal ${categoryColorClass(cat)}`}
                              variant="outline"
                            >
                              {skill}
                            </Badge>
                          ))
                        )}
                        {Object.keys(visibleSkills).length === 0 && (
                          <span className="text-xs text-muted-foreground">No skills</span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-medium text-muted-foreground">
                      {entry.totalSkills}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
